import { Stack, Typography, alpha } from '@mui/material';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import { useTranslation } from 'react-i18next';

import { theme } from '../../../../theme/ThemeProvider';
import { StatusDot } from '../StatusDot/StatusDot';

interface InsertTheConnectorProps {
  /**
   * Plug types this station accepts, straight off the station record. Which
   * plug to reach for is the one thing the driver standing at the post
   * actually needs and cannot work out from the screen.
   */
  connectorTypes?: string[];
}

/**
 * Shown while the connector is Available: the driver has paid nothing and
 * plugged nothing in yet, and the page is waiting on the charger to report
 * `Preparing`.
 *
 * This used to be a top-down drawing of a car with a plug graphic sliding
 * toward it on a 3s loop. The animation was the loudest thing on a screen
 * whose whole job is to wait quietly, and the illustration cost 253px on a
 * layout that clips — so on a short phone it was cropped by the bottom
 * navigation. Both are gone: instruction, plug type, and a dot that says the
 * page is still listening.
 */
export const InsertTheConnector: React.FC<InsertTheConnectorProps> = ({
  connectorTypes,
}) => {
  const { t } = useTranslation();

  const types = (connectorTypes ?? []).filter(Boolean);
  // Two keys rather than one with a joined list: "Type 2 connector" reads
  // correctly, "Type 2 / CCS2 connector" does not.
  const typeLabel =
    types.length === 1
      ? t('chargeStationPage.connectorTypeOne', { type: types[0] })
      : types.length > 1
        ? t('chargeStationPage.connectorTypeMany', { types: types.join(' / ') })
        : null;

  return (
    <Stack alignItems="center" sx={{ width: '100%', gap: '16px' }}>
      <Stack alignItems="center" sx={{ gap: '6px' }}>
        <Typography variant="h2" color="common.white" textAlign="center">
          {t('chargeStationPage.plugInTitle')}
        </Typography>
        <Typography
          variant="body1"
          textAlign="center"
          sx={{
            color: theme.palette.gray[100],
            // Holds the sentence to two comfortable lines instead of one long
            // measure running the full width of the phone.
            maxWidth: '30ch',
          }}
        >
          {t('chargeStationPage.plugInHint')}
        </Typography>
      </Stack>

      {typeLabel && (
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            gap: '6px',
            padding: '8px 14px',
            borderRadius: '15px',
            // Deliberately not a green pill: the connector card above already
            // spends green on the Available status, and a second green pill
            // would read as a second status rather than a piece of reference.
            backgroundColor: 'rgba(255, 255, 255, 0.06)',
            border: `1px solid ${alpha(theme.palette.gray.main, 0.18)}`,
          }}
        >
          <BoltRoundedIcon
            sx={{ fontSize: 18, color: theme.palette.green.main }}
          />
          <Typography
            variant="body1"
            color="common.white"
            sx={{ fontWeight: 600 }}
          >
            {typeLabel}
          </Typography>
        </Stack>
      )}

      <Stack direction="row" alignItems="center" sx={{ gap: '8px' }}>
        <StatusDot color={theme.palette.green.main} />
        <Typography
          variant="body2"
          sx={{ fontSize: '13px', color: theme.palette.gray[50] }}
        >
          {t('chargeStationPage.waitingForCharger')}
        </Typography>
      </Stack>
    </Stack>
  );
};
